import React, { useState, useEffect, useRef } from 'react';
import "./TextField.css";
import BodySmallRegular from './BodySmallRegular';
import { Eye, EyeOff, CircleAlert } from "lucide-react";

const MIN_CHARS_DEFAULT = 3;
const COLORS = {
  ICON: "#4B5563",
  LABEL: "#1E293B"
};
const ICON_SIZE = {
  DEFAULT: 20,
  ERROR: 16
};
const INPUT_TYPES = {
  TEXT: "text",
  PASSWORD: "password"
};
const BUTTON_TYPE = "button";

const TextField = ({
  error,
  iconPasswordStart,
  value,
  onChange,
  disabled = false,
  placeholder = '',
  label,
  type = INPUT_TYPES.TEXT,
  iconStart,
  iconEnd,
  name,
  style = {},
  className = "",
  enableSuggestions = false,
  suggestions = [],
  minCharsForSuggestions = MIN_CHARS_DEFAULT,
  onSuggestionInputChange = null,
  renderSuggestions = null
}) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [displaySuggestions, setDisplaySuggestions] = useState(false);
  const suggestionRef = useRef(null);
  const inputRef = useRef(null);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionRef.current &&
        !suggestionRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setDisplaySuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleTextChange = (e) => {
    if (onChange) {
      onChange(e);
    }
    if (enableSuggestions && e.target.value.length >= minCharsForSuggestions) {
      if (onSuggestionInputChange) {
        onSuggestionInputChange(e.target.value);
      }
      setDisplaySuggestions(true);
    } else {
      setDisplaySuggestions(false);
    }
  };

  const handleTogglePassword = () => {
    setIsPasswordVisible((prev) => !prev);
  };

  const inputType = type === INPUT_TYPES.PASSWORD && isPasswordVisible ? INPUT_TYPES.TEXT : type;
  
  return (
    <div className={`text-field-container ${className}`} style={{ position: 'relative', ...style }}>
      {label && <p className="label-text">{label}</p>}
      <div className="text-field-main">
        <div className={`input-field-container ${disabled ? 'input-field-container-disabled' : ''}`}>
          {iconStart && iconStart}
          {iconPasswordStart && type === INPUT_TYPES.PASSWORD && (
            <button
              type={BUTTON_TYPE}
              className="toggle-password-button"
              onClick={handleTogglePassword}
              disabled={disabled}
            >
              {isPasswordVisible ? 
                <Eye color={COLORS.ICON} height={ICON_SIZE.DEFAULT} /> : 
                <EyeOff color={COLORS.ICON} height={ICON_SIZE.DEFAULT} />}
            </button>
          )}
          <input
            ref={inputRef}
            className="input-field"
            type={inputType}
            disabled={disabled}
            placeholder={placeholder}
            value={value}
            onChange={handleTextChange}
            name={name}
            autoComplete="off"
          />
          {!iconPasswordStart && type === INPUT_TYPES.PASSWORD && (
            <button
              type={BUTTON_TYPE}
              className="toggle-password-button"
              onClick={handleTogglePassword}
              disabled={disabled}
            >
              {isPasswordVisible ? 
                <Eye color={COLORS.ICON} height={ICON_SIZE.DEFAULT} /> : 
                <EyeOff color={COLORS.ICON} height={ICON_SIZE.DEFAULT} />}
            </button>
          )}
          {iconEnd && iconEnd}
        </div>

        {enableSuggestions && displaySuggestions && suggestions && suggestions.length > 0 && (
          <div
            ref={suggestionRef}
            className="suggestions-container"
          >
            <div className="suggestions-inner">
              {renderSuggestions ?
                renderSuggestions(suggestions) :
                suggestions.map((item, index) => (
                  <div key={index} className="suggestion-item">
                    {typeof item === 'string' ? item : (item.name || JSON.stringify(item))}
                  </div>
                ))
              }
            </div>
          </div>
        )}

        {error && (
          <div className="error-text-container">
            <CircleAlert height={ICON_SIZE.ERROR} />
            <span className="error-text">{error}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TextField;